import MessageIcon from "@mui/icons-material/Message";
import { Box, Button } from "@mui/material";
import React, { type ReactNode } from "react";

import { isTerminalTicketStatus } from "src/constants";
import {
  AnalyticsCustomEventNameEnum,
  type TicketFragment,
  TicketStatusEnum,
} from "src/graphql/generated/types";
import { CreateTicket } from "src/pages/_shared/CreateTicket";
import { logCustomEvent } from "src/utils";

import { buttonStyle, separatorStyle } from "../../styles";
import { ActionsSection } from "../ActionsSection";

export type TicketFooterKind = "cancelled" | "closed" | "approved" | "awaitingApproval";

type FooterTicket = Pick<
  TicketFragment,
  "status" | "cancelledAt" | "customerApprovalDate" | "canBeReopened"
>;

export const getTicketFooterKind = (
  ticket: FooterTicket,
  hasRestrictedAccess: boolean
): TicketFooterKind | null => {
  if (ticket.cancelledAt) {return "cancelled";}
  if (ticket.status === TicketStatusEnum.Closed) {return "closed";}
  if (ticket.customerApprovalDate && !hasRestrictedAccess) {return "approved";}
  if (ticket.canBeReopened && !hasRestrictedAccess && isTerminalTicketStatus(ticket.status))
    {return "awaitingApproval";}
  return null;
};

type Props = {
  kind: TicketFooterKind;
  uuid: string;
  redirect: () => void;
  onApprove: () => void;
  onContinue: () => void;
};

export const TicketFooter = ({ kind, uuid, redirect, onApprove, onContinue }: Props) => {
  const newTicketButton = (
    <CreateTicket
      uuid={uuid}
      redirect={redirect}
      renderButton={({ handleOpen }) => (
        <Button
          startIcon={<MessageIcon />}
          variant="contained"
          onClick={() => {
            handleOpen();
            logCustomEvent(AnalyticsCustomEventNameEnum.TicketMessagesNewTicket);
          }}
        >
          Nouvelle demande
        </Button>
      )}
    />
  );

  const footerContent: Record<TicketFooterKind, { message: string; actions?: ReactNode }> = {
    cancelled: {
      message:
        "En raison de la fin du contrat de syndic de votre immeuble, votre demande a été automatiquement fermée.",
    },
    closed: {
      message:
        "Compte tenu des délais passés, votre demande a été archivée. Toutefois, si vous souhaitez nous contacter à ce sujet ou un nouveau, n'hésitez pas à ouvrir une nouvelle demande.",
      actions: newTicketButton,
    },
    approved: {
      message: "Votre demande est clôturée.",
      actions: newTicketButton,
    },
    awaitingApproval: {
      message: "Votre demande a été traitée.",
      actions: (
        <>
          <Button variant="contained" color="success" sx={buttonStyle} onClick={onApprove}>
            Merci, je confirme la clôture de la demande
          </Button>
          <Box sx={separatorStyle} />
          <Button variant="contained" color="info" sx={buttonStyle} onClick={onContinue}>
            Poursuivre ma demande
          </Button>
        </>
      ),
    },
  };

  const { message, actions } = footerContent[kind];

  return <ActionsSection text={message} rightNode={actions} />;
};
